import React, { useState, useEffect, useRef } from 'react';
import styles from "./LoffaTemplateModal.module.css";
import { LFade, LDialog, LButton, LFormInput } from '@loffa/gpp-modules';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faCheck, faX } from '@fortawesome/free-solid-svg-icons';

//wrapper of "Edit" & "Create" loffa template modals
const LoffaTemplateModalBase = ({
  isOpen,
  onClose,
  products,
  title,
  onSave,
  fetchInitialData,
  initialData
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [currentStepLabel, setCurrentStepLabel] = useState("");
  const [selectedProduct, setSelectedProduct] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [columns, setColumns] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);
  const initializedRef = useRef(false);
  const dataTypes = ['VarChar', 'Boolean', 'DateTime', 'Integer', 'Decimal']; //datatypes user can choose from for column data

  useEffect(() => {
    if (isOpen && fetchInitialData && !initializedRef.current) {
      fetchInitialData();
      initializedRef.current = true;
    }
  }, [isOpen, fetchInitialData]);

  //useEffects handles auto-setting formdata when user edits a loffa template. only runs when editting
  useEffect(() => {
    if (initialData && products.length > 0 && !hasInitialized) {
      const product = products.find(p => p.id === initialData.productType);
      setSelectedProduct(product || '');
      setTemplateName(initialData.name);
      const mappedColumns = initialData.dataTemplateColumns.map(col => ({
        ...col,
        isExpanded: false, 
        minLength: col.minLength != null ? col.minLength : '',
        maxLength: col.maxLength != null ? col.maxLength : '',
      }));
      setColumns(mappedColumns);
      setHasInitialized(true);
    }
  }, [initialData, products, hasInitialized]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setSelectedProduct('');
      setTemplateName('');
      setColumns([]);
      setErrorMessage('');
      setHasInitialized(false);
      initializedRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (currentStep === 1) { setCurrentStepLabel("Select Product")}
    if (currentStep === 2) { setCurrentStepLabel("Template Name")}
    if (currentStep === 3) { setCurrentStepLabel("Template Columns")}
    if (currentStep === 4) { setCurrentStepLabel("Verify & Save")}
  }, [currentStep]);

  const handleNext = () => {
    setErrorMessage('');
    setCurrentStep(prev => (prev < 4 ? prev + 1 : prev));
  };

  const handleBack = () => {
    setErrorMessage('');
    setCurrentStep(prev => (prev > 1 ? prev - 1 : prev));
  };

  //handles adding a column to the selected template
  const addColumn = () => {
    if (columns.length > 0) {
      const lastCol = columns[columns.length - 1];
      if (lastCol.isExpanded && !lastCol.name.trim()) {
        setErrorMessage('Please fill in the column name before adding a new column');
        return;
      }
      lastCol.isExpanded = false
    }
    setErrorMessage('');
    setColumns((prev) => [
      ...prev,
      {
        name: '',
        required: false,
        minLength: '',
        maxLength: '',
        type: 'VarChar',
        index: prev.length,
        isExpanded: true,
      },
    ]);
  };

  const removeColumn = index => {
    const updated = columns.filter((_, i) => i !== index);
    const reindexed = updated.map((col, i) => ({ ...col, index: i }));
    setColumns(reindexed);
  };

  const updateColumn = (index, field, value) => {
    setColumns(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  //called when "up"/"down" icon is clicked - increases/decreases the columns index by 1
  const moveColumn = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === columns.length - 1)) return;
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    setColumns(prev => {
      const newColumns = [...prev];
      [newColumns[index], newColumns[swapIndex]] = [newColumns[swapIndex], newColumns[index]];
      return newColumns.map((col, i) => ({ ...col, index: i }));
    });
  };

  //handles expanding a template column to show its fill details
  const toggleColumnExpansion = index => {
    debugger
    const column = columns[index];
    if (column.isExpanded) {
      if (!column.name.trim()) {
        setErrorMessage('Missing column name');
        return;
      }
    }
    setColumns(prev => {
      const updated = [...prev];
      updated[index].isExpanded = !updated[index].isExpanded;
      return updated;
    });
  };

  const validateForm = () => {
    const isValid = columns.every(col => {
      if (!col.name || !col.type) return false;
      if (col.type !== 'DateTime' && col.type !== 'Boolean') {
        const min = col.minLength !== '' ? parseInt(col.minLength, 10) : 0;
        const max = col.maxLength !== '' ? parseInt(col.maxLength, 10) : 1000;
        if (isNaN(min) || min < 0 || isNaN(max) || max < min) return false;
      }
      return true;
    });

    return !!selectedProduct?.id && !!templateName && isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setErrorMessage('Please fill in all required fields correctly');
      return;
    }
    const error = await onSave({
      selectedProduct,
      templateName,
      columns
    });
    if (error) {
      setErrorMessage(error);
    } else {
      onClose();
    }
  };

  const renderStepContent = () => {
    switch(currentStep) {
      case 1:
        return (
          <div>
            <LFormInput
              type="combobox"
              labelName="Product Type"
              labelAlign="top"
              comboBox={{
                data: products,
                textField: "name",
                dataItemKey: "id",
                value: selectedProduct,
                onChange: e => setSelectedProduct(e.value),
                placeholder: "-- Choose a product type --"
              }}
            />
          </div>
        );
      case 2:
        return (
          <div>
            <LFormInput
              type="input"
              labelName="Template Name"
              labelAlign="top"
              required
              input={{
                name: "templateName",
                value: templateName,
                onChange: e => setTemplateName(e.target.value),
                placeholder: "Enter template name"
              }}
            />
          </div>
        );
      case 3:
        return (
          <div className={`${styles.loffaDataTemplateStepContainer} ${styles.loffaTemplateDialogBody}`}>
            <p>Define Template Columns</p>
            {columns.map((col, index) => (
              <div key={index} className={`${styles.loffaDataTemplateHeaderRow} ${styles.loffaTemplateFormInput}`}>
                {col.isExpanded ? ( 
                  <> {/* if expanded, show all inputs. otherwise, show preview */}
                    <LFormInput
                      type="input"
                      labelName="Index #"
                      input={{
                        value: col.index,
                        disabled:true
                      }}
                    />
                    <LFormInput
                      type="input"
                      labelName="Name"
                      input={{
                        value: col.name,
                        onChange: e => updateColumn(index, 'name', e.target.value),
                        placeholder: "Enter Column name"
                      }}
                    />
                    <LFormInput
                      type="combobox"
                      labelName="Data Type"
                      comboBox={{
                        data: dataTypes,
                        value: col.type,
                        onChange: e => updateColumn(index, 'type', e.value),
                        placeholder: "Select data type"
                      }}
                    />
                    {(col.type !== 'DateTime' && col.type !== 'Boolean') && (
                      <>
                        <LFormInput
                          type="input"
                          labelName="Min Length"
                          input={{
                            type: "number",
                            value: col.minLength,
                            onChange: e => updateColumn(index, 'minLength', e.target.value),
                            min: "0",
                            placeholder: "0"
                          }}
                        />
                        <LFormInput
                          type="input"
                          labelName="Max Length"
                          input={{
                            type: "number",
                            value: col.maxLength,
                            onChange: e => updateColumn(index, 'maxLength', e.target.value),
                            min: col.minLength || "0",
                            placeholder: "0"
                          }}
                        />
                      </>
                    )}
                    <LFormInput
                      type="checkbox"
                      labelName="Required"
                      checkbox={{
                        checked: col.required,
                        onChange: e => updateColumn(index, 'required', e.value),
                        value: col.required
                      }}
                    />
                    <div className={styles.loffaDataTemplateActions}>
                      <button
                        className={styles.loffaDataTemplateActionButton}
                        onClick={() => moveColumn(index, 'up')}
                        disabled={index === 0}
                      >
                        <FontAwesomeIcon icon={faArrowUp} /> {/* moves column up 1 index */}
                      </button>
                      <button
                        className={styles.loffaDataTemplateActionButton}
                        onClick={() => moveColumn(index, 'down')}
                        disabled={index === columns.length - 1}
                      >
                        <FontAwesomeIcon icon={faArrowDown} /> {/* moves column down 1 index */}
                      </button>
                      <button
                        className={styles.loffaDataTemplateActionButton}
                        style={{ color: '#ea3333' }}
                        onClick={() => removeColumn(index)}
                      >
                        <FontAwesomeIcon icon={faX} /> {/* removes column */}
                      </button>
                      <button
                        className={styles.loffaDataTemplateActionButton}
                        onClick={() => toggleColumnExpansion(index)}
                      >
                        <FontAwesomeIcon icon={faCheck} /> {/* adds column to state */}
                    </button>
                    </div>
                  </>
                ) : ( 
                  <div 
                    className={styles.loffaDataTemplateColumnSnippet} 
                    onClick={() => toggleColumnExpansion(index)}
                  >
                    #{col.index} - <strong>{col.name || 'Unnamed Column'}</strong> - {col.type}
                    {col.type !== 'DateTime' && col.type !== 'Boolean' && 
                      ` - Min: ${col.minLength || 0}, Max: ${col.maxLength || "N/A"}`}
                    {col.required ? ' - Required' : ''}
                  </div>
                )}
              </div>
            ))}
            <button onClick={addColumn} className={styles.loffaDataTemplateAddColumnButton}>
              + Add Column
            </button>
          </div>
        );
      case 4: //final step shows a conformation window 
        return (
            <div className={`${styles.loffaDataTemplateColumnVerificationList} ${styles.loffaTemplateDialogBody}`}>
                <p>Confirm Your Template</p>
                <p><strong>Product Type:</strong> {selectedProduct?.name}</p>
                <p><strong>Template Name:</strong> {templateName}</p>
                <p><strong>Columns:</strong></p>
                <ul>
                    {columns.map(col => (
                    <li key={col.index}>
                        {col.index}. <b>{col.name}</b> (Type: {col.type}
                        {(col.type !== 'DateTime' && col.type !== 'Boolean') 
                        ? `, Min: ${col.minLength || 0}, Max: ${col.maxLength || "N/a"}`
                        : ''}, Required: <b>{col.required ? 'Yes' : 'No'}</b>)
                    </li>
                    ))}
                </ul>
            </div>
        )
      default:
        return null;
    }
  };

  return (
    <LFade transitionEnterDuration={300}>
      {isOpen && (
        <LDialog
          width="750"
          title={title}
          subtitle={`Step ${currentStep} of 4 - ${currentStepLabel}`}
          body={(
            <div className={styles.loffaDataTemplateDialogBody}>
              {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
              {renderStepContent()}
            </div>
          )}
          action={(
            <>
              {currentStep > 1 && <LButton onClick={handleBack}>Back</LButton>}
              {currentStep < 4 && <LButton themeColor="primary" onClick={handleNext}>Next</LButton>}
              {currentStep === 4 && ( //"Save" button shows on final step - confirmation page
                <LButton themeColor="primary" onClick={handleSave}>
                  Save
                </LButton>
              )}
            </>
          )}
          actionCloseButton={onClose}
          actionOverlay={onClose}
        />
      )}
    </LFade>
  );
};

export default LoffaTemplateModalBase;